import { Plugin, PluginKey } from 'hubspot-prosemirror/pm/state';
import { Decoration, DecorationSet } from 'hubspot-prosemirror/pm/view';
import ReactDOM from 'react-dom';
import uuid from 'hubspot-prosemirror/prosemirror/utils/uuid';
import ContentAssistantDropdown from './CADropdown';
import { initContentAssistantSlashPluginState } from './utils';
import { jsx as _jsx } from "react/jsx-runtime";
export const createContentAssistantSlashPlugin = function createContentAssistantSlashPlugin({
  portalApi,
  commands,
  onInsert,
  getSelectedCommandParams,
  trackInteraction,
  usageDescription
}) {
  const uniqueKey = uuid();
  const pluginKey = new PluginKey(`contentAssistantSlashPlugin_${uniqueKey}`);

  // content assistant popup
  let dropdownContainer;
  let dropdownContainerPortal;
  const createContentAssistantDropdown = function createContentAssistantDropdown(view) {
    if (dropdownContainer && dropdownContainerPortal) {
      return dropdownContainer;
    }
    dropdownContainer = document.createElement('span');
    dropdownContainerPortal = /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/_jsx(ContentAssistantDropdown, {
      view: view,
      onInsert: onInsert,
      getSelectedCommandParams: getSelectedCommandParams,
      trackInteraction: trackInteraction,
      usageDescription: usageDescription,
      options: commands,
      pluginKey: pluginKey
    }), dropdownContainer, uniqueKey);
    portalApi.createPortal(dropdownContainerPortal);
    return dropdownContainer;
  };
  const closeDropdown = () => {
    if (dropdownContainerPortal) {
      portalApi.destroyPortal(dropdownContainerPortal.key);
      dropdownContainerPortal = null;
    }
  };
  const cleanUp = () => {
    closeDropdown();
    if (dropdownContainer) {
      dropdownContainer.remove();
    }
  };
  return new Plugin({
    key: pluginKey,
    state: {
      init() {
        return initContentAssistantSlashPluginState();
      },
      apply(tr, value) {
        const meta = tr.getMeta(pluginKey);
        if (meta) {
          return meta;
        }
        return value;
      }
    },
    props: {
      handleClick(view) {
        const tr = view.state.tr;
        view.dispatch(tr.setMeta(pluginKey, initContentAssistantSlashPluginState()).setMeta('addToHistory', false));
      },
      handleKeyDown(view, e) {
        const state = pluginKey.getState(view.state);
        const tr = view.state.tr;
        if (state && !state.dropdownActive) {
          if (e.key !== '/') {
            return false;
          }
          const $position = view.state.selection.$from;
          if (!$position.nodeBefore || $position.nodeBefore.text && $position.nodeBefore.text.slice(-1) === ' ') {
            view.dispatch(tr.setMeta(pluginKey, Object.assign({}, initContentAssistantSlashPluginState(), {
              dropdownActive: true,
              searchTextStartPos: $position.pos,
              currentCursorPos: $position.pos + 1,
              currentDropdownIndex: 0
            })).setMeta('addToHistory', false));
          }
        } else if (state && state.dropdownActive) {
          switch (e.key) {
            case 'ArrowUp':
              {
                view.dispatch(tr.setMeta(pluginKey, Object.assign({}, state, {
                  currentDropdownIndex: Math.max(0, state.currentDropdownIndex - 1)
                })).setMeta('addToHistory', false));
                return true;
              }
            case 'ArrowDown':
              {
                view.dispatch(tr.setMeta(pluginKey, Object.assign({}, state, {
                  currentDropdownIndex: Math.min(state.currentDropdownIndex + 1, Object.keys(commands).length - 1)
                })).setMeta('addToHistory', false));
                return true;
              }
            case 'Escape':
              {
                view.dispatch(tr.setMeta(pluginKey, initContentAssistantSlashPluginState()).setMeta('addToHistory', false));
                return true;
              }
            case 'Enter':
              {
                view.dispatch(tr.setMeta(pluginKey, Object.assign({}, state, {
                  popoverActive: true
                })).setMeta('addToHistory', false));
                return true;
              }
            case 'Backspace':
              {
                view.dispatch(tr.setMeta(pluginKey, Object.assign({}, state, {
                  searchTerm: state.searchTerm.slice(0, -1),
                  currentCursorPos: state.currentCursorPos
                })).setMeta('addToHistory', false));
                return false;
              }
            default:
              break;
          }
          view.dispatch(tr.setMeta(pluginKey, initContentAssistantSlashPluginState()).setMeta('addToHistory', false));
        }
        return false;
      },
      // to decorate the currently active @mention text
      decorations(editorState) {
        const pluginState = pluginKey.getState(editorState);
        const {
          dropdownActive,
          popoverActive
        } = pluginState;
        if (!dropdownActive && !popoverActive) {
          closeDropdown();
          return null;
        }
        return DecorationSet.create(editorState.doc, [Decoration.widget(editorState.selection.from, view => createContentAssistantDropdown(view), {
          side: 0,
          destroy(node) {
            if (!node.isConnected) {
              closeDropdown();
            }
          }
        })]);
      }
    },
    view() {
      return {
        update: (view, prevState) => {
          const $position = view.state.selection.$from;
          const $prevPosition = prevState.selection.$from;
          const state = pluginKey.getState(view.state);
          const tr = view.state.tr;

          // handle when current cursor is moved before initial /
          if (state.searchTextStartPos === $position.pos && $prevPosition.pos > $position.pos) {
            view.dispatch(tr.setMeta(pluginKey, initContentAssistantSlashPluginState()).setMeta('addToHistory', false));
          }
        },
        destroy: () => {
          cleanUp();
        }
      };
    }
  });
};